import { Routes, UrlTree } from '@angular/router';
import { PageNotFoundComponent } from './shared/components/page-not-found/page-not-found.component';
import { HomeComponent } from './home/home.component';
import { LoginComponent } from './auth/login/login.component';
import { SignedOutComponent } from './base/signed-out/signed-out.component';
import { AboutComponent } from './about/about.component';
import { SettingsComponent } from './settings/settings.component';
import { ProjectsComponent } from './projects/projects.component';
import { isLoggedInGuard } from './guards/is-logged-in.guard';
import { RolesComponent } from './admin/roles/roles.component';
import { PermissionsComponent } from './admin/permissions/permissions.component';
import { UsersComponent } from './users/users/users.component';
import { projectsResolver } from './resolvers/projects.resolver';
import { usersResolver } from './resolvers/users.resolver';
import { turnOnIndicatorResolver } from './resolvers/turn-on-indicator.resolver';
import { ResetPwComponent } from './reset-pw/reset-pw.component';
import { ControlParamsComponent } from './control-params/control-params.component';
import { controlParamsResolver } from './resolvers/control-params.resolver';
import { TrackerComponent } from './tracker/tracker/tracker.component';
import {
  alignJustifyIcon,
  parametersUnknownIcon,
  SVGIcon,
  windowIcon,
} from "@progress/kendo-svg-icons";
import { ImportCsvComponent } from './files/import-csv/import-csv.component';
import { ContactUsComponent } from './common-pages/contact-us/contact-us.component';
import { PrivacyComponent } from './common-pages/privacy/privacy.component';
import { DisclaimerComponent } from './common-pages/disclaimer/disclaimer.component';
import { TermsCheckComponent } from './common-pages/terms-conditions/terms-check.component';
import { HomeLoggedOutComponent } from './home-logged-out/home-logged-out.component';
import { inject } from '@angular/core';
import { AuthTokenService } from './auth/auth-token.service';
import { homeSelectGuard } from './guards/home-select.guard';
import { CasiProjectsComponent } from './casi-projects/casi-projects.component';
import { casiStoryStatusResolver } from './resolvers/casi-story-status.resolver';
import { casiStoryTypesResolver } from './resolvers/casi-story-types.resolver';
import { casiStoryPrioritiesResolver } from './resolvers/casi-story-priorities.resolver';
import { casiStoryEstimatesResolver } from './resolvers/casi-story-estimates.resolver';
import { UserStoryListComponent } from './user-stories/user-story-list/user-story-list.component';
import { ProjectTrackerComponent } from './tracker/project-tracker/project-tracker.component';
import { projectLabelsResolver } from './resolvers/project-labels.resolver';
import { TestRouteParamComponent } from './tests/test-route-param/test-route-param.component';
import { UserStoryViewComponent } from './user-stories/user-story-view/user-story-view.component';
import { ProjectSummaryComponent } from './project-summary/project-summary.component';
import { ChatComponent } from './chat/chat.component';
import { EnquiresListComponent } from './admin/enquires-list/enquires-list.component';

const wIcon: SVGIcon = windowIcon;
const alignIcon: SVGIcon = alignJustifyIcon;
const parametersIcon: SVGIcon = parametersUnknownIcon;

export const routes: Routes = [
  {
    path: 'home',
    canActivate: [homeSelectGuard],
    component: HomeComponent
  },
  {
    path: 'home-logged-out',
    component: HomeLoggedOutComponent
  },
  {
    title: 'Login', path: 'login',
    component: LoginComponent
  },
  {
    title: 'Signed Out', path: 'signed-out',
    component: SignedOutComponent
  },
  {
    title: 'About', path: 'about',
    component: AboutComponent
  },
  {
    title: 'Settings', path: 'settings',
    component: SettingsComponent
  },
  {
    title: 'Projects', path: 'projects',
    component: ProjectsComponent,
    canActivate: [isLoggedInGuard],
    resolve: {
      turnOn: turnOnIndicatorResolver,
      users: usersResolver
    },

  },
  {
    title: 'Casi Projects', path: 'casi-projects',
    component: CasiProjectsComponent
  },
  {
    title: 'Permission', path: 'permissions',
    component: PermissionsComponent,
    canActivate: [isLoggedInGuard]
  },
  {
    title: 'Roles', path: 'roles',
    component: RolesComponent,
    canActivate: [isLoggedInGuard]
  },
  {
    title: 'Users', path: 'users',
    component: UsersComponent,
    canActivate: [isLoggedInGuard]
  },
  {
    title: 'Project Summary and Releases', path: 'projectsummary',
    component: ProjectSummaryComponent,
    canActivate: [isLoggedInGuard]
  },
  {
    title: 'Control Params', path: 'controlparams',
    component: ControlParamsComponent,
    canActivate: [isLoggedInGuard],
    resolve: {
      controlParams: controlParamsResolver
    }

  },
  {
    title: 'Reset Password',
    path: 'reset-password',
    component: ResetPwComponent
  },
  {
    path: 'testCsvImport',
    component: ImportCsvComponent
  },
  {
    path: 'chat',
    component: ChatComponent
  },
  {
    path: '',
    // redirectTo: () => {
    //   return `/home`;
    // },
    redirectTo: 'home',
    // redirectTo: function (): "/home" | "/home-logged-out" {
    //   const authTokenService = inject(AuthTokenService);
    //   return authTokenService.isLoggedIn ? '/home' : '/home-logged-out';
    // },
    pathMatch: 'full'
  },
  // {
  //   path: '',
  //   redirectTo: 'home',
  //   pathMatch: 'full'
  // },
  {
    path: 'tracker',
    title: 'Tracker',
    component: TrackerComponent,
    canActivate: [isLoggedInGuard],
    resolve: {
      turnOn: turnOnIndicatorResolver,
      projects: projectsResolver,
      users: usersResolver,
      storyStatus: casiStoryStatusResolver,
      storyTypes: casiStoryTypesResolver,
      storyPriorities: casiStoryPrioritiesResolver,
      storyEstimates: casiStoryEstimatesResolver
    },
  },
  {
    path: 'userstories',
    title: 'User Stories',
    component: UserStoryListComponent,
    canActivate: [isLoggedInGuard],
    resolve: {
      turnOn: turnOnIndicatorResolver,
      projects: projectsResolver,
      users: usersResolver,
      storyStatus: casiStoryStatusResolver,
      storyTypes: casiStoryTypesResolver,
      storyPriorities: casiStoryPrioritiesResolver,
      storyEstimates: casiStoryEstimatesResolver,
      projectLabels: projectLabelsResolver
    }
  },
  {
    path: 'userstory/show/:id',
    title: 'User Story Details',
    component: UserStoryViewComponent,
    canActivate: [isLoggedInGuard],
    resolve: {
      turnOn: turnOnIndicatorResolver,
      projects: projectsResolver,
      users: usersResolver,
      storyStatus: casiStoryStatusResolver,
      storyTypes: casiStoryTypesResolver,
      storyPriorities: casiStoryPrioritiesResolver,
      storyEstimates: casiStoryEstimatesResolver,
      projectLabels: projectLabelsResolver
    }
  },
  {
    path: 'project-tracker',
    title: 'Project Tracker',
    component: ProjectTrackerComponent,
    canActivate: [isLoggedInGuard],
    resolve: {
      turnOn: turnOnIndicatorResolver,
      projects: projectsResolver,
      users: usersResolver,
      storyStatus: casiStoryStatusResolver,
      storyTypes: casiStoryTypesResolver,
      storyPriorities: casiStoryPrioritiesResolver,
      storyEstimates: casiStoryEstimatesResolver,
      projectLabels: projectLabelsResolver
    },
  },
  { title: 'Contact Us', path: 'contact-us', component: ContactUsComponent },
  { title: 'New Licence Agreement', path: 'terms-check', component: TermsCheckComponent },
  { title: 'Privacy Policy', path: 'privacy', component: PrivacyComponent },
  { title: 'Terms of Use', path: 'disclaimer', component: DisclaimerComponent },
  { title: 'Manage Enquiries', path: 'admin-enquires-list', component: EnquiresListComponent, canActivate: [isLoggedInGuard] },
  { path: 'test-router/:id', component: TestRouteParamComponent },
  {
    path: '**',
    component: PageNotFoundComponent
  }
];

